/**
 * `ProvenanceLink` — every displayed fact reaches its evidence.
 *
 * The other component `PROJECT_STRUCTURE.md` names, for the same reason: a feature that
 * renders an uncited fact has quietly regressed the product.
 *
 * It takes an **assertion id, never an optional one**. That is the whole design: a
 * component that accepted `undefined` would be a component a feature could render for a
 * fact it could not trace, and TypeScript would say nothing. The M6 exit criterion —
 * evidence in at most two clicks — is enforced here, at the type level.
 */

import type { ReactElement } from 'react';
import { Link } from 'react-router-dom';

export interface ProvenanceLinkProps {
  /** The case the assertion belongs to. */
  caseId: string;
  /** **Required.** The assertion behind whatever is being displayed. */
  assertionId: string;
  /** Optional label; defaults to a short form of the id. */
  label?: string;
  /** Render as an icon-sized affordance, for a dense table or a graph tooltip. */
  compact?: boolean;
}

/**
 * Link a displayed fact to the assertion it came from.
 *
 * @param props - The case and assertion.
 * @returns The link.
 */
export function ProvenanceLink({
  caseId,
  assertionId,
  label,
  compact = false,
}: ProvenanceLinkProps): ReactElement {
  return (
    <Link
      to={`/cases/${caseId}/evidence?assertion=${assertionId}`}
      data-testid="provenance-link"
      data-assertion-id={assertionId}
      className="inline-flex items-center gap-1 rounded text-accent underline-offset-2 hover:underline"
      title="Open the evidence behind this claim"
    >
      <span aria-hidden="true">🔗</span>
      {!compact && <span className="font-mono text-xs">{label ?? assertionId.slice(0, 8)}</span>}
      <span className="sr-only">Open the evidence behind this claim</span>
    </Link>
  );
}
